import React, { useState, useEffect } from 'react';
import {
  MdRouter, MdAdd, MdEdit, MdDelete, MdClose, MdWifi, MdWifiOff,
  MdPlayArrow, MdStop, MdRefresh, MdFileDownload, MdSettings,
  MdSignalCellularAlt, MdLocationOn, MdQrCodeScanner, MdCheckCircle,
  MdError, MdSync, MdNfc, MdBluetoothSearching,
  MdSettingsInputAntenna, MdHistory, MdCloudUpload, MdContentCopy, MdAutorenew, MdSave,
  MdHealthAndSafety,
} from 'react-icons/md';
import api from '../api/axios';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import { confirm } from '../lib/dialogs';
import { backendBase } from '../lib/basePath';
import SearchableSelect from '../components/SearchableSelect';

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16 }}>×</button>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    online:   { color: '#4CAF50', bg: '#e8f5e9', label: 'Online' },
    offline:  { color: '#9e9e9e', bg: '#f5f5f5', label: 'Offline' },
    reading:  { color: '#2196F3', bg: '#e3f2fd', label: 'Reading' },
    error:    { color: '#f44336', bg: '#ffebee', label: 'Error' },
  };
  const s = map[status] || map.offline;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color,
    }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: s.color,
        animation: status === 'reading' ? 'pulse 1s infinite' : 'none',
      }} />
      {s.label}
    </span>
  );
};

const READER_TYPES = ['fixed', 'handheld', 'portal', 'drone'];
const MODES = ['inventory', 'location', 'access-control'];
const IMPINJ_MODELS = ['Speedway R120', 'Speedway R220', 'Speedway R420', 'Speedway R700', 'xSpan Gateway', 'xArray', 'xPortal', 'Speedway Connect', 'Other'];

const defaultForm = {
  reader_name: '', reader_type: 'fixed', serial_number: '', ip_address: '',
  port: 5084, model: '', location_id: '', username: 'root', password: 'impinj',
  antenna_count: 4, tx_power: 30.0, rx_sensitivity: -70.0, session: 2,
  mode: 'inventory', notes: ''
};

const ReaderManager = () => {
  const [readers, setReaders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editReader, setEditReader] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [connecting, setConnecting] = useState({});
  const [tagResults, setTagResults] = useState({});
  const [activePanel, setActivePanel] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Detail drawer (tabs: antennas | events | auto-mode)
  const [drawer, setDrawer] = useState(null); // { reader, tab }
  const [antennas, setAntennas] = useState([]);
  const [events, setEvents] = useState([]);
  const [pushToken, setPushToken] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchReaders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/readers');
      setReaders(res.data);
    } catch { showToast('Failed to load readers', 'error'); }
    setLoading(false);
  };

  const fetchLocations = async () => {
    try { const r = await api.get('/locations'); setLocations(r.data); } catch {}
  };

  useEffect(() => { fetchReaders(); fetchLocations(); }, []);

  const openAdd = () => { setEditReader(null); setForm(defaultForm); setShowModal(true); };
  const openEdit = (r) => {
    setEditReader(r);
    setForm({
      reader_name: r.reader_name || '', reader_type: r.reader_type || 'fixed',
      serial_number: r.serial_number || '', ip_address: r.ip_address || '',
      port: r.port || 5084, model: r.model || '', location_id: r.location_id || '',
      username: r.username || 'root', password: r.password || 'impinj',
      antenna_count: r.antenna_count || 4, tx_power: r.tx_power || 30.0,
      rx_sensitivity: r.rx_sensitivity || -70.0, session: r.session || 2,
      mode: r.mode || 'inventory', notes: r.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reader_name) { showToast('Reader name is required', 'error'); return; }
    setSubmitting(true);
    try {
      if (editReader) {
        await api.put(`/readers/${editReader.id}`, form);
        showToast('Reader updated');
      } else {
        await api.post('/readers', form);
        showToast('Reader added');
      }
      setShowModal(false);
      fetchReaders();
    } catch (err) { showToast(err.response?.data?.error || 'Save failed', 'error'); }
    setSubmitting(false);
  };

  const handleDelete = async (r) => {
    if (!(await confirm(`Delete reader "${r.reader_name}"?`))) return;
    try { await api.delete(`/readers/${r.id}`); showToast('Reader deleted'); fetchReaders(); }
    catch { showToast('Delete failed', 'error'); }
  };

  const handleConnect = async (r) => {
    setConnecting(c => ({ ...c, [r.id]: true }));
    try {
      const res = await api.post(`/readers/${r.id}/connect`);
      showToast(`Connected to ${r.reader_name}`);
      fetchReaders();
    } catch (err) {
      showToast(err.response?.data?.error || `Cannot reach ${r.reader_name}`, 'error');
      fetchReaders();
    }
    setConnecting(c => ({ ...c, [r.id]: false }));
  };

  const handleStart = async (r) => {
    setConnecting(c => ({ ...c, [`start_${r.id}`]: true }));
    try {
      await api.post(`/readers/${r.id}/start`);
      showToast(`Started reading on ${r.reader_name}`);
      fetchReaders();
    } catch (err) { showToast(err.response?.data?.error || 'Start failed', 'error'); }
    setConnecting(c => ({ ...c, [`start_${r.id}`]: false }));
  };

  const handleStop = async (r) => {
    try {
      await api.post(`/readers/${r.id}/stop`);
      showToast(`Stopped reading on ${r.reader_name}`);
      fetchReaders();
    } catch (err) { showToast(err.response?.data?.error || 'Stop failed', 'error'); }
  };

  const handleGetTags = async (r) => {
    setConnecting(c => ({ ...c, [`tags_${r.id}`]: true }));
    try {
      const res = await api.get(`/readers/${r.id}/tags`);
      setTagResults(t => ({ ...t, [r.id]: res.data.tags || [] }));
      setActivePanel(r.id);
      showToast(`Retrieved ${(res.data.tags || []).length} tag(s)`);
    } catch (err) { showToast(err.response?.data?.error || 'Failed to get tags', 'error'); }
    setConnecting(c => ({ ...c, [`tags_${r.id}`]: false }));
  };

  // ── Drawer (per-reader detail tabs) ─────────────────────────────────────
  const openDrawer = async (reader, tab) => {
    setDrawer({ reader, tab });
    if (tab === 'antennas') {
      try {
        const r = await api.get(`/readers/${reader.id}/antennas`);
        setAntennas(r.data);
      } catch (e) { showToast('Failed to load antennas', 'error'); }
    } else if (tab === 'events') {
      try {
        const r = await api.get(`/readers/${reader.id}/events?limit=100`);
        setEvents(r.data);
      } catch (e) { showToast('Failed to load events', 'error'); }
    } else if (tab === 'auto-mode') {
      try {
        const r = await api.get(`/readers/${reader.id}`);
        setPushToken(r.data.push_token);
      } catch (e) { showToast('Failed to load token', 'error'); }
    }
  };
  const closeDrawer = () => { setDrawer(null); setAntennas([]); setEvents([]); setPushToken(null); };

  const updateAntenna = async (antennaId, patch) => {
    if (!drawer) return;
    try {
      const r = await api.put(`/readers/${drawer.reader.id}/antennas/${antennaId}`, patch);
      setAntennas(prev => prev.map(a => a.antenna_id === antennaId ? { ...a, ...r.data } : a));
    } catch (err) { showToast(err.response?.data?.error || 'Update failed', 'error'); }
  };

  const handleHealthCheck = async (r) => {
    try {
      const res = await api.post(`/readers/${r.id}/health-check`);
      showToast(res.data.ok ? `${r.reader_name} is reachable` : `Health: ${res.data.error}`,
        res.data.ok ? 'success' : 'warning');
      fetchReaders();
    } catch (err) {
      showToast(err.response?.data?.error || 'Health check failed', 'error');
    }
  };

  const handleRegenerateToken = async (r) => {
    if (!(await confirm(`Regenerate push token for ${r.reader_name}? The old token will stop working immediately.`))) return;
    try {
      const res = await api.post(`/readers/${r.id}/regenerate-push-token`);
      setPushToken(res.data.push_token);
      showToast('Push token regenerated');
    } catch (err) { showToast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    showToast('Copied to clipboard');
  };

  const handleExport = async () => {
    try {
      // Forward the on-screen filters so the download matches the table the
      // operator is looking at. Blank = no filter, same semantics as `filtered`
      // below and as the backend's optional ?type=/?status= params.
      const qs = new URLSearchParams();
      if (filterType) qs.set('type', filterType);
      if (filterStatus) qs.set('status', filterStatus);
      const url = qs.toString()
        ? `/readers/export/excel?${qs.toString()}`
        : '/readers/export/excel';
      await downloadFromApi(url, tsFilename('readers', 'xlsx'));
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const filtered = readers.filter(r => {
    if (filterType && r.reader_type !== filterType) return false;
    if (filterStatus && r.status !== filterStatus) return false;
    return true;
  });

  const stats = [
    { label: 'Total Readers', value: readers.length, color: 'var(--primary)' },
    { label: 'Online', value: readers.filter(r => r.status === 'online' || r.status === 'reading').length, color: 'var(--success)' },
    { label: 'Reading', value: readers.filter(r => r.status === 'reading').length, color: 'var(--accent)' },
    { label: 'Offline / Error', value: readers.filter(r => r.status === 'offline' || r.status === 'error').length, color: 'var(--danger)' },
  ];

  const typeIcon = (type) => {
    if (type === 'handheld') return <MdQrCodeScanner />;
    if (type === 'portal') return <MdBluetoothSearching />;
    if (type === 'drone') return <MdNfc />;
    return <MdRouter />;
  };

  return (
    <div className="page-container">
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .reader-card { background:white; border-radius:12px; border:1.5px solid var(--border-light); padding:18px; box-shadow:var(--shadow-sm); transition:all 0.2s; }
        .reader-card:hover { box-shadow:var(--shadow); transform:translateY(-2px); }
        .reader-card.online { border-left: 4px solid #4CAF50; }
        .reader-card.reading { border-left: 4px solid #2196F3; }
        .reader-card.error { border-left: 4px solid #f44336; }
      `}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title"><MdRouter /> Reader Manager</div>
          <div className="page-subtitle">Configure and monitor RFID readers (Impinj Speedway, xSpan, handheld devices)</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={fetchReaders}><MdRefresh /> Refresh</button>
          <button className="btn btn-success btn-sm" onClick={handleExport}><MdFileDownload /> Export</button>
          <button className="btn btn-primary" onClick={openAdd}><MdAdd /> Add Reader</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', marginBottom: 20 }}>
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.6rem' }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchableSelect className="form-control" style={{ width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {READER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </SearchableSelect>
        <SearchableSelect className="form-control" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="online">Online</option>
          <option value="reading">Reading</option>
          <option value="offline">Offline</option>
          <option value="error">Error</option>
        </SearchableSelect>
        {(filterType || filterStatus) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setFilterType(''); setFilterStatus(''); }}>Clear</button>
        )}
      </div>

      {/* Readers Grid */}
      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <MdRouter style={{ fontSize: '3rem', opacity: 0.3 }} />
            <h3>No readers configured</h3>
            <p>Add your first RFID reader to get started</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openAdd}><MdAdd /> Add Reader</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {filtered.map(r => (
            <div key={r.id} className={`reader-card ${r.status}`}>
              {/* Card Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: r.status === 'online' || r.status === 'reading' ? '#e3f2fd' : '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: r.status === 'online' || r.status === 'reading' ? 'var(--accent)' : 'var(--text-muted)',
                    fontSize: 22,
                  }}>
                    {typeIcon(r.reader_type)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.reader_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {r.model || 'Unknown model'} · {r.reader_type}
                    </div>
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>

              {/* Details Grid */}
              <div className="grid-2col-auto" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginBottom: 14 }}>
                {(() => {
                  const fwOutdated = r.latest_firmware && r.firmware_version &&
                    r.latest_firmware !== r.firmware_version;
                  const fwValue = r.firmware_version
                    ? (fwOutdated
                        ? <span style={{ color: '#ef6c00' }}>{r.firmware_version} ⚠ ({r.latest_firmware} avail)</span>
                        : r.firmware_version)
                    : '—';
                  return [
                    { label: 'IP Address', value: r.ip_address || '—' },
                    { label: 'Port', value: r.port || 5084 },
                    { label: 'Serial No.', value: r.serial_number || '—' },
                    { label: 'Firmware', value: fwValue },
                    { label: 'Antennas', value: r.antenna_count || 4 },
                    { label: 'TX Power', value: r.tx_power ? `${r.tx_power} dBm` : '—' },
                    { label: 'Auto-mode', value: r.auto_mode_enabled ? <span style={{ color: '#2e7d32' }}>ON</span> : 'off' },
                    { label: 'Auto-move', value: r.auto_create_movements ? <span style={{ color: '#2e7d32' }}>ON</span> : 'off' },
                  ].map(d => (
                    <div key={d.label}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{d.label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{d.value}</div>
                    </div>
                  ));
                })()}
              </div>

              {r.location_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <MdLocationOn style={{ color: 'var(--warning)' }} />
                  {r.location_name}
                </div>
              )}

              {r.last_seen && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Last seen: {new Date(r.last_seen).toLocaleString()}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid var(--border-light)', paddingTop: 12 }}>
                <button className="btn btn-sm btn-outline" onClick={() => handleConnect(r)}
                  disabled={connecting[r.id]} title="Ping / Connect to reader">
                  {connecting[r.id] ? <><div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Connecting…</> : <><MdWifi /> Connect</>}
                </button>

                {(r.status === 'online') && (
                  <button className="btn btn-sm btn-accent" onClick={() => handleStart(r)}
                    disabled={connecting[`start_${r.id}`]} title="Start RFID inventory">
                    {connecting[`start_${r.id}`] ? '…' : <><MdPlayArrow /> Start</>}
                  </button>
                )}

                {r.status === 'reading' && (
                  <button className="btn btn-sm btn-warning" onClick={() => handleStop(r)} title="Stop reading">
                    <MdStop /> Stop
                  </button>
                )}

                {(r.status === 'online' || r.status === 'reading') && (
                  <button className="btn btn-sm btn-success" onClick={() => handleGetTags(r)}
                    disabled={connecting[`tags_${r.id}`]} title="Fetch buffered tag reads">
                    {connecting[`tags_${r.id}`] ? '…' : <><MdSync /> Get Tags</>}
                  </button>
                )}

                <button className="btn btn-sm btn-outline" onClick={() => handleHealthCheck(r)} title="Manual health check">
                  <MdHealthAndSafety />
                </button>
                <button className="btn btn-sm btn-outline" onClick={() => openDrawer(r, 'antennas')} title="Per-antenna config">
                  <MdSettingsInputAntenna />
                </button>
                <button className="btn btn-sm btn-outline" onClick={() => openDrawer(r, 'events')} title="Reader event log">
                  <MdHistory />{r.recent_alerts > 0 && <span style={{ marginLeft: 4, color: '#ef6c00', fontWeight: 700 }}>{r.recent_alerts}</span>}
                </button>
                <button className="btn btn-sm btn-outline" onClick={() => openDrawer(r, 'auto-mode')} title="Auto-mode push setup">
                  <MdCloudUpload />
                </button>
                <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)} title="Edit configuration">
                  <MdEdit />
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)} title="Delete reader">
                  <MdDelete />
                </button>
              </div>

              {/* Tag Results Panel */}
              {activePanel === r.id && tagResults[r.id] !== undefined && (
                <div style={{
                  marginTop: 12, background: '#0d2447', borderRadius: 8,
                  padding: '10px 12px', maxHeight: 200, overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ color: '#64b5f6', fontSize: 12, fontWeight: 700 }}>
                      TAG READS ({tagResults[r.id].length})
                    </span>
                    <button onClick={() => { setActivePanel(null); }} style={{ background: 'none', border: 'none', color: '#64b5f6', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                  {tagResults[r.id].length === 0 ? (
                    <div style={{ color: '#aaa', fontSize: 12 }}>No tags in buffer</div>
                  ) : tagResults[r.id].map((tag, i) => (
                    <div key={i} style={{
                      padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                      fontSize: 11, fontFamily: 'monospace', color: '#a5d6a7'
                    }}>
                      {tag.epc || tag.epcHex || tag.tagId || JSON.stringify(tag)}
                      {tag.rssi !== undefined && <span style={{ color: '#78909c', marginLeft: 10 }}>RSSI: {tag.rssi}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <span className="modal-title">{editReader ? 'Edit Reader' : 'Add New Reader'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}><MdClose /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Basic Info */}
                <div style={{ marginBottom: 8, fontWeight: 700, fontSize: 12, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Basic Information
                </div>
                <div className="form-row" style={{ marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Reader Name *</label>
                    <input className="form-control" placeholder="e.g. Warehouse A Fixed Reader"
                      value={form.reader_name} onChange={e => setForm(f => ({ ...f, reader_name: e.target.value }))} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Reader Type</label>
                    <SearchableSelect className="form-control" value={form.reader_type} onChange={e => setForm(f => ({ ...f, reader_type: e.target.value }))}>
                      {READER_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </SearchableSelect>
                  </div>
                </div>

                <div className="form-row" style={{ marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Model</label>
                    <SearchableSelect className="form-control" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}>
                      <option value="">Select model…</option>
                      {IMPINJ_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                    </SearchableSelect>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Serial Number</label>
                    <input className="form-control" placeholder="e.g. 1234567890" value={form.serial_number}
                      onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
                  </div>
                </div>

                {/* Network */}
                <div style={{ marginBottom: 8, marginTop: 16, fontWeight: 700, fontSize: 12, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Network Configuration
                </div>
                <div className="form-row" style={{ marginBottom: 12, gridTemplateColumns: '2fr 1fr' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">IP Address</label>
                    <input className="form-control" placeholder="192.168.1.100" value={form.ip_address}
                      onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Port</label>
                    <input type="number" className="form-control" value={form.port}
                      onChange={e => setForm(f => ({ ...f, port: parseInt(e.target.value) || 5084 }))} />
                  </div>
                </div>
                <div className="form-row" style={{ marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Username</label>
                    <input className="form-control" value={form.username}
                      onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                  </div>
                </div>

                {/* RF Settings */}
                <div style={{ marginBottom: 8, marginTop: 16, fontWeight: 700, fontSize: 12, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  RF / Antenna Settings
                </div>
                <div className="grid-4col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Antennas</label>
                    <input type="number" className="form-control" min={1} max={32} value={form.antenna_count}
                      onChange={e => setForm(f => ({ ...f, antenna_count: parseInt(e.target.value) || 4 }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">TX Power (dBm)</label>
                    <input type="number" className="form-control" step={0.5} value={form.tx_power}
                      onChange={e => setForm(f => ({ ...f, tx_power: parseFloat(e.target.value) || 30 }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">RX Sensitivity</label>
                    <input type="number" className="form-control" step={1} value={form.rx_sensitivity}
                      onChange={e => setForm(f => ({ ...f, rx_sensitivity: parseFloat(e.target.value) || -70 }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Session</label>
                    <SearchableSelect className="form-control" value={form.session} onChange={e => setForm(f => ({ ...f, session: parseInt(e.target.value) }))}>
                      {[0, 1, 2, 3].map(s => <option key={s} value={s}>Session {s}</option>)}
                    </SearchableSelect>
                  </div>
                </div>

                <div className="form-row" style={{ marginBottom: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Read Mode</label>
                    <SearchableSelect className="form-control" value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}>
                      {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </SearchableSelect>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Location</label>
                    <SearchableSelect className="form-control" value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))}>
                      <option value="">No location</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
                    </SearchableSelect>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes} placeholder="Installation notes, remarks…"
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : (editReader ? 'Update Reader' : 'Add Reader')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drawer: per-reader detail tabs (antennas / events / auto-mode) */}
      {drawer && (
        <div className="modal-overlay" onClick={closeDrawer}>
          <div className="modal" style={{ maxWidth: 720, width: '90%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {drawer.tab === 'antennas' && <><MdSettingsInputAntenna /> Antennas — {drawer.reader.reader_name}</>}
                {drawer.tab === 'events' && <><MdHistory /> Event Log — {drawer.reader.reader_name}</>}
                {drawer.tab === 'auto-mode' && <><MdCloudUpload /> Auto-Mode Push — {drawer.reader.reader_name}</>}
              </div>
              <button className="btn btn-icon" onClick={closeDrawer}><MdClose /></button>
            </div>

            {/* Tab nav */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
              {['antennas', 'events', 'auto-mode'].map(t => (
                <button key={t} onClick={() => openDrawer(drawer.reader, t)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '10px 16px', fontSize: 13, fontWeight: 600,
                    color: drawer.tab === t ? 'var(--accent)' : '#888',
                    borderBottom: drawer.tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                  }}>
                  {t === 'antennas' ? 'Antennas' : t === 'events' ? 'Event Log' : 'Auto-Mode'}
                </button>
              ))}
            </div>

            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {drawer.tab === 'antennas' && (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Enabled</th>
                        <th>Read Power (dBm)</th>
                        <th>Write Power (dBm)</th>
                        <th>Location override</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {antennas.map(a => (
                        <tr key={a.id}>
                          <td><strong>Ant {a.antenna_id}</strong></td>
                          <td>
                            <input type="checkbox" checked={!!a.enabled}
                              onChange={e => updateAntenna(a.antenna_id, { enabled: e.target.checked })} />
                          </td>
                          <td>
                            <input type="number" step="0.5" value={a.read_power_dbm}
                              onChange={e => updateAntenna(a.antenna_id, { read_power_dbm: parseFloat(e.target.value) || 0 })}
                              style={{ width: 80, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }} />
                          </td>
                          <td>
                            <input type="number" step="0.5" value={a.write_power_dbm}
                              onChange={e => updateAntenna(a.antenna_id, { write_power_dbm: parseFloat(e.target.value) || 0 })}
                              style={{ width: 80, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }} />
                          </td>
                          <td>
                            <SearchableSelect value={a.location_id || ''}
                              onChange={e => updateAntenna(a.antenna_id, { location_id: e.target.value || null })}
                              style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12 }}>
                              <option value="">— inherit reader —</option>
                              {locations.map(l => <option key={l.id} value={l.id}>{l.location_code}</option>)}
                            </SearchableSelect>
                          </td>
                          <td>
                            <input type="text" value={a.notes || ''}
                              onChange={e => updateAntenna(a.antenna_id, { notes: e.target.value })}
                              placeholder="e.g. north door"
                              style={{ width: 140, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                    Changes are saved automatically as you edit each cell.
                    To change the antenna count, edit the reader's main config.
                  </div>
                </div>
              )}

              {drawer.tab === 'events' && (
                <div>
                  {events.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
                      No events yet
                    </div>
                  ) : events.map(ev => (
                    <div key={ev.id} style={{
                      display: 'flex', gap: 10, padding: '10px 0',
                      borderBottom: '1px solid #f0f2f5',
                    }}>
                      <span style={{
                        flexShrink: 0, padding: '2px 8px', borderRadius: 10,
                        background: ev.severity === 'error' ? '#ffebee' : ev.severity === 'warning' ? '#fff3e0' : '#e8f5e9',
                        color: ev.severity === 'error' ? '#c62828' : ev.severity === 'warning' ? '#ef6c00' : '#2e7d32',
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        height: 'fit-content',
                      }}>{ev.event_type}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13 }}>{ev.message || '(no message)'}</div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          {new Date(ev.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {drawer.tab === 'auto-mode' && (
                <div style={{ padding: 4 }}>
                  <p style={{ fontSize: 13, color: '#444', marginTop: 0 }}>
                    Configure your reader to push tag reads to this URL. The reader can use HTTP POST with JSON body
                    or a TCP socket; both are accepted at the same backend.
                  </p>

                  <div style={{ background: '#f8f9fb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
                      HTTP push endpoint
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                        {`${window.location.protocol}//${window.location.host.replace(':3000', ':5001')}${backendBase()}/api/readers/auto-mode/push`}
                      </code>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => copyToClipboard(`${window.location.protocol}//${window.location.host.replace(':3000', ':5001')}${backendBase()}/api/readers/auto-mode/push`)}>
                        <MdContentCopy />
                      </button>
                    </div>
                  </div>

                  <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#7a5400', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 }}>
                      Push token (keep secret)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#7a5400' }}>
                        {pushToken || '(loading…)'}
                      </code>
                      <button className="btn btn-outline btn-sm"
                        onClick={() => copyToClipboard(pushToken)} disabled={!pushToken}>
                        <MdContentCopy />
                      </button>
                      <button className="btn btn-warning btn-sm" onClick={() => handleRegenerateToken(drawer.reader)}>
                        <MdAutorenew /> Regenerate
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={!!drawer.reader.auto_mode_enabled}
                        onChange={async (e) => {
                          await api.put(`/readers/${drawer.reader.id}`, { auto_mode_enabled: e.target.checked });
                          setDrawer(d => ({ ...d, reader: { ...d.reader, auto_mode_enabled: e.target.checked } }));
                          fetchReaders();
                        }} />
                      <strong>Auto-mode enabled</strong>
                      <span style={{ fontSize: 11, color: '#888' }}>Mark this reader as configured for push (informational; doesn't change reader behaviour)</span>
                    </label>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={!!drawer.reader.auto_create_movements}
                        onChange={async (e) => {
                          await api.put(`/readers/${drawer.reader.id}`, { auto_create_movements: e.target.checked });
                          setDrawer(d => ({ ...d, reader: { ...d.reader, auto_create_movements: e.target.checked } }));
                          fetchReaders();
                        }} />
                      <strong>Auto-create movements</strong>
                      <span style={{ fontSize: 11, color: '#888' }}>When a known asset's tag is detected at this reader's location, automatically create an asset_movements row</span>
                    </label>
                  </div>

                  <details style={{ marginTop: 16 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Example curl</summary>
                    <pre style={{ background: '#0d1b33', color: '#a5d6a7', padding: 12, borderRadius: 6, fontSize: 11, overflowX: 'auto', marginTop: 8 }}>{`curl -X POST \\
  -H "Authorization: Bearer ${pushToken || '<TOKEN>'}" \\
  -H "Content-Type: application/json" \\
  -d '{"epc":"E280BEEFCAFE0001","ant":1,"rssi":-52}' \\
  ${window.location.protocol}//${window.location.host.replace(':3000', ':5001')}${backendBase()}/api/readers/auto-mode/push`}</pre>
                  </details>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={closeDrawer}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReaderManager;
